import { useMemo } from 'react';
import { useQuery } from '../hooks/useQuery';
import { api } from '../lib/api';
import { fmtCurrency, fmtMonth } from '../lib/fmt';
import {
  MetricCard, SectionLabel, Card, CardHeader, Table, Th, Tr, Td, Badge,
  Avatar, initials, Spinner, EmptyState,
} from '../components/ui';
import { ChartCard, OrgTrendChart } from '../components/charts';

// Max thresholds for the progress bar — pulled from spec §4.2. Could be
// replaced with a call to /admin/tiers/year-summary to reflect overrides.
const MAX_BY_ROLE: Record<string, number> = { IC: 31500, DM: 100000, SA: 100000 };

const MONTHS_BEFORE = 9;
const MONTHS_AHEAD = 3;

interface TrendBar { month: string; shipped: number; wip: number; isCurrent: boolean; isFuture: boolean }

export default function AdminOverviewPage() {
  const { data, isLoading, error } = useQuery(() => api.admin.orgSummary());

  // All hooks must run on every render — must not sit behind early returns.
  const currentMonth = data?.current_month ?? null;
  const periods = data?.periods ?? [];

  const trendMonths: string[] = useMemo(() => {
    if (!currentMonth) return [];
    const [yStr, mStr] = currentMonth.split('-');
    const y = parseInt(yStr);
    const m = parseInt(mStr);
    const out: string[] = [];
    for (let offset = -MONTHS_BEFORE; offset <= MONTHS_AHEAD; offset++) {
      const d = new Date(Date.UTC(y, m - 1 + offset, 1));
      out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
    }
    return out;
  }, [currentMonth]);

  const periodByMonth = useMemo(() => {
    const map = new Map<string, typeof periods[number]>();
    for (const p of periods) map.set(p.attribution_month, p);
    return map;
  }, [periods]);

  const trend: TrendBar[] = useMemo(
    () =>
      trendMonths.map((m) => {
        const p = periodByMonth.get(m);
        return {
          month: m,
          shipped: Number(p?.total_shipped_value ?? 0),
          wip: Number(p?.total_wip_value ?? 0),
          isCurrent: m === currentMonth,
          isFuture: !!currentMonth && m > currentMonth,
        };
      }),
    [trendMonths, periodByMonth, currentMonth],
  );

  if (isLoading) return <Spinner />;
  if (error) return <EmptyState message={error} />;
  if (!data) return <EmptyState message="No data" />;

  const current = periods.find((p) => p.attribution_month === data.current_month)
    ?? (data.current_month
      ? { attribution_month: data.current_month, total_shipped_value: 0, total_wip_value: 0, is_locked: false, employee_count: 0, employees_on_tier: 0 }
      : undefined);
  const currentUsers = data.current_users ?? [];
  const onTierCount = currentUsers.filter((u) => u.bonus_tier != null).length;

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 10 }}>
        <MetricCard
          label={`Org revenue shipped — ${current ? fmtMonth(current.attribution_month) : '—'}`}
          value={fmtCurrency(Number(current?.total_shipped_value ?? 0))}
          accent="green"
          sub={current?.is_locked ? 'Locked' : 'Each deliverable counted once'}
        />
        <MetricCard
          label="WIP pipeline"
          value={fmtCurrency(Number(current?.total_wip_value ?? 0))}
          accent="amber"
          sub="Unshipped work due this month"
        />
        <MetricCard
          label="Employees on tier"
          value={`${onTierCount} / ${currentUsers.length}`}
          accent="blue"
          sub={current ? fmtMonth(current.attribution_month) : undefined}
        />
        <MetricCard
          label="Active engagements"
          value={data.active_engagement_count}
          sub="Across all clients"
        />
      </div>

      <ChartCard
        title="Org trend — shipped + WIP"
        sub={`Previous ${MONTHS_BEFORE} months · current · next ${MONTHS_AHEAD} months planned`}
        height={240}
      >
        {trend.length === 0 ? (
          <EmptyState message="No periods yet — trigger a refresh to populate." />
        ) : (
          <OrgTrendChart
            data={trend.map((b) => ({
              attribution_month: b.month,
              shipped: b.shipped,
              wip: b.wip,
              isCurrent: b.isCurrent,
              isFuture: b.isFuture,
            }))}
          />
        )}
      </ChartCard>
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
        Current month rendered in brand red for quick orientation; future months at reduced opacity indicate planned (not yet shipped) pipeline.
        Past months with WIP on top = unshipped work planned to have completed by then.
      </div>

      <div>
        <SectionLabel>
          Bonus eligibility — {current ? fmtMonth(current.attribution_month) : 'current period'}
        </SectionLabel>
        <Card>
          <CardHeader>
            <span style={{ fontSize: 12, fontWeight: 500 }}>All active employees</span>
            <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
              Per-user bonus attribution — does NOT sum to org revenue above
            </span>
          </CardHeader>
          {currentUsers.length === 0 ? (
            <EmptyState message="No bonus calculations yet. Trigger a refresh to populate." />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Role</Th>
                  <Th>Team</Th>
                  <Th right>Shipped value</Th>
                  <Th>Tier</Th>
                  <Th>Progress</Th>
                </tr>
              </thead>
              <tbody>
                {currentUsers.map((u, i) => {
                  const max = MAX_BY_ROLE[u.role] ?? 0;
                  const pct = max ? Math.min(100, Math.round((u.shipped_value / max) * 100)) : 0;
                  return (
                    <Tr key={u.jira_user_id}>
                      <Td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Avatar initials={initials(u.display_name)} size={22} index={i} />
                          {u.display_name}
                        </div>
                      </Td>
                      <Td><Badge variant="gray">{u.role}</Badge></Td>
                      <Td muted style={{ fontSize: 11 }}>{u.team || '—'}</Td>
                      <Td right bold>{fmtCurrency(u.shipped_value)}</Td>
                      <Td>
                        {u.bonus_tier ? <Badge variant="green">Tier {u.bonus_tier}</Badge> : <Badge variant="gray">Below</Badge>}
                      </Td>
                      <Td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 80, height: 4, background: 'var(--color-border)', borderRadius: 2, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: 'var(--color-blue)', borderRadius: 2 }} />
                          </div>
                          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{max ? `${pct}%` : 'n/a'}</span>
                        </div>
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card>
        <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 6, lineHeight: 1.4 }}>
          Shipped value per row follows the spec's bonus rules (§4.2): ICs earn on hours they logged; DMs and SAs earn on the full hours of deliverables they manage.
          One shipped deliverable therefore attributes to multiple employees (author IC + the DM + the SA), so summing this column exceeds the org revenue number at the top — that's intentional and drives each role's bonus independently.
        </div>
      </div>
    </>
  );
}
